import { forwardRef, useState, useEffect, useRef } from 'react';

import ApprovalLineTable from '@features/document-write/components/ApprovalLineTable/ApprovalLineTable';
import AttachmentSection from '@features/document-write/components/AttachmentSection/AttachmentSection';
import CKEditorViewer from '@features/document-write/components/CKEditorViewer/CKEditorViewer';
import DocumentInfoForm from '@features/document-write/components/DocumentInfoForm/DocumentInfoForm';
import ReferenceDocumentListSection from '@features/document-write/components/ReferenceDocumentListSection/ReferenceDocumentListSection';
import SummaryNoteSection from '@features/document-write/components/SummaryNoteSection/SummaryNoteSection';
import chevronLeftIcon from '@shared/assets/images/chevron-left_icon.svg';
import chevronRightIcon from '@shared/assets/images/chevron-right_icon.svg';

import styles from './DocumentPreview.module.scss';

/**
 * 캡처된 본문 PNG 슬라이스 영역.
 * - PNG 가로는 항상 baseWidth(734)로 고정 캡처되고, 넓은 표는 캡처 측 CSS 로 컨테이너 안에 눌러 맞춰진다.
 * - 따라서 표시 측에서는 별도 스케일 보정 없이 슬라이스 top/containerHeight 를 그대로 사용.
 *
 * @param {Object} props
 * @param {string} props.imageUrl - PNG data URL
 * @param {{ top: number, containerHeight: number }} props.imageSlice - 슬라이스(캡처 레이아웃 CSS px 기준)
 */
function CapturedEditorImageSection({ imageUrl, imageSlice }) {
  return (
    <div
      className={styles.editorSection}
      style={{
        height: imageSlice.containerHeight,
        overflow: 'hidden',
        position: 'relative',
        flex: 'none',
      }}
    >
      <img
        src={imageUrl}
        alt="본문 내용"
        className={styles.capturedEditorImage}
        style={{
          position: 'absolute',
          top: -imageSlice.top,
          left: 0,
          width: '100%',
          display: 'block',
        }}
      />
    </div>
  );
}

/**
 * DocumentPreview 컴포넌트
 * - 문서 미리보기 화면
 * - Figma 디자인 기반: node-id=1909-9708
 *
 * [인쇄 기능 구현]
 * - react-to-print 라이브러리를 사용하여 인쇄 기능 제공
 * - forwardRef로 래핑하여 인쇄할 DOM 요소에 ref를 전달할 수 있도록 함
 * - 다른 컴포넌트에서 인쇄 기능을 구현할 때도 동일하게 forwardRef를 사용해야 함
 *
 * [다중 페이지 미리보기]
 * - 첫 페이지: 모든 문서 정보 + 결재 라인 + 본문 일부
 * - 두 번째 페이지 이후: 간소화된 헤더(제목만) + 본문 이어지는 부분 + 요약/첨부/참조문서
 * - 페이지 네비게이션 버튼으로 페이지 이동 가능
 */
const DocumentPreview = forwardRef(function DocumentPreview(
  {
    title,
    documentInfo,
    editorContent,
    editorContentByPage, // 페이지별로 분리된 에디터 콘텐츠
    summaryNote,
    attachments,
    referenceDocuments,
    useCorporateSeal,
    onClose,
    onPrint,
    docIdx,
    tabType,
    initialTotalPages, // 부모 컴포넌트에서 계산된 페이지 수
    editorImageUrl, // 이미지 기반 렌더링 시 사용될 전체 이미지 URL
    editorImageSlices, // 이미지 슬라이스 정보 [{top, height, containerHeight}]
  },
  ref,
) {
  // 페이지 상태 관리 (1부터 시작)
  const [currentPage, setCurrentPage] = useState(1);
  // 실제 페이지 수 (이미지 슬라이스가 있으면 그 개수 사용, 아니면 부모 전달값)
  const [totalPages, setTotalPages] = useState(editorImageSlices?.length || initialTotalPages || 1);
  // 두 페이지 보기 모드 상태
  const [isTwoPageMode, setIsTwoPageMode] = useState(false);
  // react-to-print용 ref
  const contentRef = useRef(null);

  // 부모에서 전달받은 페이지 정보가 변경되면 업데이트
  useEffect(
    function () {
      if (editorImageSlices?.length) {
        setTotalPages(editorImageSlices.length);
      } else if (initialTotalPages !== undefined && initialTotalPages !== null) {
        setTotalPages(initialTotalPages);
      }
    },
    [initialTotalPages, editorImageSlices],
  );

  // 페이지 네비게이션 핸들러
  function handlePreviousPage() {
    setCurrentPage(function (prev) {
      // 두 페이지 모드에서는 2페이지씩 이동
      var step = isTwoPageMode ? 2 : 1;
      return Math.max(1, prev - step);
    });
  }

  function handleNextPage() {
    setCurrentPage(function (prev) {
      // 두 페이지 모드에서는 2페이지씩 이동
      var step = isTwoPageMode ? 2 : 1;
      return Math.min(totalPages, prev + step);
    });
  }

  // 두 페이지 보기 모드 토글 핸들러
  function handleToggleTwoPageMode() {
    setIsTwoPageMode(function (prev) {
      var newMode = !prev;
      // 모드 전환 시 현재 페이지를 홀수로 맞춤 (두 페이지 모드에서 좌측 페이지는 항상 홀수)
      if (newMode && currentPage % 2 === 0) {
        setCurrentPage(currentPage - 1);
      }
      return newMode;
    });
  }

  // 첫 페이지 여부
  const isFirstPage = currentPage === 1;
  // 마지막 페이지 여부 (두 페이지 모드에서는 현재 또는 다음 페이지가 마지막인 경우)
  const isLastPage = isTwoPageMode
    ? currentPage >= totalPages || currentPage + 1 >= totalPages
    : currentPage >= totalPages;
  // 다중 페이지 여부
  const isMultiPage = totalPages > 1;
  // 중간 페이지 여부 (첫 페이지도 마지막 페이지도 아닌 경우)
  const isMiddlePage = isMultiPage && !isFirstPage && !isLastPage;
  // 두 페이지 모드에서 우측 페이지 번호
  const rightPageNum = currentPage + 1;
  // 두 페이지 모드에서 우측 페이지가 존재하는지 여부
  const hasRightPage = rightPageNum <= totalPages;

  /**
   * 페이지 내부 콘텐츠 렌더링 함수
   * @param {number} pageNum - 페이지 번호 (1부터 시작)
   */
  function renderPageContent(pageNum) {
    const isFirst = pageNum === 1;
    const isLast = pageNum >= totalPages;
    const pageContent =
      editorContentByPage && editorContentByPage.length > 0
        ? editorContentByPage[pageNum - 1] || ''
        : editorContent || '';

    // 이미지 슬라이스 정보가 있는 경우 이미지로 렌더링
    const imageSlice = editorImageSlices ? editorImageSlices[pageNum - 1] : null;

    return (
      <>
        {/* 첫 페이지: 전체 헤더 */}
        {isFirst && (
          <div className={`${styles.modalHeader} ${styles.firstPageHeader}`}>
            <div className={styles.headerTitle}>
              <h2 className={styles.title}>{title || '품의서'}</h2>
            </div>
          </div>
        )}

        {/* 이후 페이지: 헤더 생략 (첫 페이지만 제목 표시) */}
        {/* {!isFirst && (
          <div className={`${styles.modalHeader} ${styles.subsequentPageHeader}`}>
            <div className={styles.headerTitle}>
              <h2 className={styles.title}>{title || '품의서'}</h2>
            </div>
          </div>
        )} */}

        {/* 첫 페이지: 문서 정보 + 결재 라인 */}
        {isFirst && (
          <div className={`${styles.topSection} ${styles.firstPageTopSection}`}>
            <div className={styles.documentInfoTop}>
              <DocumentInfoForm
                documentInfo={documentInfo}
                onDocumentInfoChange={function () {}}
                isPreview={true}
                section="top"
              />
            </div>
            <div className={styles.approvalLineWrapper}>
              <ApprovalLineTable
                documentInfo={documentInfo}
                onDocumentInfoChange={function () {}}
                isPreview={true}
                docIdx={docIdx}
                tabType={tabType}
              />
            </div>
          </div>
        )}

        {/* 첫 페이지: 법인 인감 체크박스 */}
        {isFirst && useCorporateSeal !== undefined && (
          <div
            className={`${styles.corporateSealCheckbox} ${styles.firstPageCorporateSealCheckbox}`}
          >
            <input type="checkbox" checked={useCorporateSeal} readOnly disabled />
            <label>인감 날인·인감증명서 사용(법인인감/사용인감)</label>
          </div>
        )}

        {/* 첫 페이지: 참조자/시행자 + 제목 */}
        {isFirst && (
          <div className={`${styles.middleSection} ${styles.firstPageMiddleSection}`}>
            <DocumentInfoForm
              documentInfo={documentInfo}
              onDocumentInfoChange={function () {}}
              isPreview={true}
              section="bottom"
            />
          </div>
        )}

        {/* 본문 에디터 - 이미지 기반 또는 HTML 기반 */}
        {imageSlice && editorImageUrl ? (
          <CapturedEditorImageSection imageUrl={editorImageUrl} imageSlice={imageSlice} />
        ) : (
          (pageContent || isFirst) && (
            <div className={styles.editorSection}>
              <CKEditorViewer
                value={pageContent}
                className={styles.editorContent}
                isPreview={true}
              />
            </div>
          )
        )}

        {/* 마지막 페이지: 요약 및 비고 + 첨부파일 */}
        {isLast && (
          <div className={styles.summaryAndAttachmentContainer}>
            <SummaryNoteSection summaryNote={summaryNote || ''} isPreview={true} />
            <div className={styles.divider} />
            <AttachmentSection attachments={attachments || []} isPreview={true} />
          </div>
        )}

        {/* 마지막 페이지: 참조문서 */}
        {isLast && (
          <div className={styles.bottomSection}>
            <div className={styles.referenceDocsWrapper}>
              <ReferenceDocumentListSection
                referenceDocuments={referenceDocuments || []}
                onReferenceDocumentsChange={function () {}}
                isPreview={true}
              />
            </div>
          </div>
        )}

        {/* 페이지 푸터 - 마지막 페이지에만 표시 */}
        {isLast && (
          <div className={styles.pageFooter}>
            <span className={styles.documentId}>{documentInfo?.documentNumber || ''}</span>
            {/* 페이지 번호(1/3 등) 제거 요청 반영 - 필요한 경우 아래 주석 해제 */}
            {/* <span className={styles.pageNumber}>
              {pageNum} / {totalPages}
            </span> */}
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.previewPage}>
      <div className={styles.previewContainer}>
        {/* 상단 헤더 */}
        <div className={`${styles.previewHeader} ${isTwoPageMode ? styles.twoPageHeader : ''}`}>
          <div className={styles.headerLeft}>
            <button type="button" className={styles.backButton} onClick={onClose}>
              뒤로가기
            </button>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={styles.twoPageButton}
              disabled={!isMultiPage}
              onClick={handleToggleTwoPageMode}
            >
              {isTwoPageMode ? '한 페이지 보기' : '두 페이지 보기'}
            </button>
            <div className={styles.pageNavButtons}>
              <button
                type="button"
                className={styles.navButton}
                onClick={handlePreviousPage}
                disabled={isFirstPage}
              >
                <img src={chevronLeftIcon} alt="이전" className={styles.navIcon} />
                <span>이전</span>
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={handleNextPage}
                disabled={isLastPage}
              >
                <span>다음</span>
                <img src={chevronRightIcon} alt="다음" className={styles.navIcon} />
              </button>
            </div>
            <button type="button" className={styles.printButton} onClick={onPrint}>
              인쇄
            </button>
          </div>
        </div>

        {/*
          문서 내용 - react-to-print의 ref 연결
          - ref를 이 div에 연결하여 react-to-print가 이 영역만 인쇄하도록 함
          - 화면 미리보기(screenPreview)와 인쇄용 컨테이너(printContainer) 분리
          - 화면에서는 현재 페이지만 표시, 인쇄 시에는 모든 페이지 출력
        */}
        <div
          ref={(node) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            contentRef.current = node;
          }}
          className={styles.printContentWrapper}
        >
          {/* [화면용] 현재 페이지만 표시 */}
          <div className={styles.screenPreview}>
            {isTwoPageMode ? (
              <div key="two-page-mode" className={styles.twoPageContainer}>
                {/* 좌측 페이지 (현재 페이지) */}
                <div
                  className={styles.previewContent}
                  data-is-multi-page={isMultiPage}
                  data-current-page={currentPage}
                  data-total-pages={totalPages}
                  data-is-last={currentPage === totalPages}
                >
                  {renderPageContent(currentPage)}
                </div>
                {/* 우측 페이지 (다음 페이지) 또는 빈 페이지 */}
                <div
                  className={`${styles.previewContent} ${!hasRightPage ? styles.emptyPage : ''}`}
                  data-is-multi-page={isMultiPage}
                  data-current-page={rightPageNum}
                  data-total-pages={totalPages}
                  data-is-last={rightPageNum === totalPages}
                >
                  {hasRightPage ? (
                    renderPageContent(rightPageNum)
                  ) : (
                    <div className={styles.emptyPageContent} />
                  )}
                </div>
              </div>
            ) : (
              // 한 페이지 보기 모드
              <div key="single-page-mode" className={styles.singlePageContainer}>
                <div
                  className={styles.previewContent}
                  data-is-multi-page={isMultiPage}
                  data-current-page={currentPage}
                  data-total-pages={totalPages}
                  data-is-last={currentPage === totalPages}
                >
                  {renderPageContent(currentPage)}
                </div>
              </div>
            )}
          </div>

          {/* [인쇄용] 무조건 1페이지부터 전체 페이지를 모두 렌더링 (화면에서는 안보임) */}
          <div className={styles.printContainer}>
            {Array.from({ length: totalPages }, function (_, index) {
              const pageNum = index + 1;
              return (
                <div
                  key={`print-page-${pageNum}`}
                  className={styles.printPage}
                  data-total-pages={totalPages}
                  data-is-last={pageNum === totalPages}
                  data-current-page={pageNum}
                >
                  {renderPageContent(pageNum)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default DocumentPreview;
